import React from "react";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  UnorderedList,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";

import { ADMIN_REQUEST_CANDIDATE_ACCOUNT_DELETION_MUTATION } from "../graphql/agencies";
import { ALL_CANDIDATES_PATH } from "../routes";

export const CONFIRMATION_WORD = "DELETE";

// Owner-admin ("super admin") only: permanently deletes a candidate's account
// on their behalf. Rendered as a "Danger zone" block so it isn't hit by accident;
// the confirm modal requires a reason and typing DELETE.
const DeleteCandidateAccountButton = ({ candidate, currentUser }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();
  const [reason, setReason] = React.useState("");
  const [confirmation, setConfirmation] = React.useState("");

  const [requestDeletion, { loading }] = useMutation(
    ADMIN_REQUEST_CANDIDATE_ACCOUNT_DELETION_MUTATION
  );

  if (!currentUser?.isOwner) {
    return null;
  }

  // A profile without a user (e.g. a Bullhorn import) has no account to delete.
  if (!candidate?.candidateProfileId || !candidate?.userId) {
    return null;
  }

  const trimmedReason = reason.trim();
  const canConfirm =
    trimmedReason.length > 0 && confirmation === CONFIRMATION_WORD && !loading;

  const handleClose = () => {
    if (loading) return;
    setReason("");
    setConfirmation("");
    onClose();
  };

  const handleDelete = async () => {
    if (!canConfirm) return;
    try {
      await requestDeletion({
        variables: {
          candidateProfileId: candidate.candidateProfileId,
          reason: trimmedReason,
        },
      });
      toast({
        title: "Candidate account deletion requested",
        description: `${candidate.fullName}'s account is being deleted.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onClose();
      // The candidate record stops resolving once deletion is requested.
      navigate(ALL_CANDIDATES_PATH, { replace: true });
    } catch (error) {
      console.error("Error deleting candidate account:", error);
      toast({
        title: "Failed to delete candidate account",
        description: error?.message || "Something went wrong.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Box
        w="100%"
        border="1px solid"
        borderColor="red.500"
        borderRadius="md"
        p={4}
      >
        <Text fontSize="lg" fontWeight="bold" color="red.400" mb={1}>
          Danger zone
        </Text>
        <Text fontSize="sm" mb={3}>
          Permanently delete this candidate&apos;s account and personal data.
          This cannot be undone.
        </Text>
        <Button colorScheme="red" size="sm" onClick={onOpen}>
          Delete candidate account
        </Button>
      </Box>

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        isCentered
        variant={"darkMode"}
        size="lg"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete candidate account</ModalHeader>
          <ModalCloseButton isDisabled={loading} />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text>
                You are about to permanently delete the account of{" "}
                <strong>{candidate.fullName}</strong>. This is what happens:
              </Text>
              <UnorderedList spacing={1} pl={2}>
                <ListItem>
                  They are signed out everywhere and can no longer log in.
                </ListItem>
                <ListItem>
                  Their personal data (name, email, phone, CV and documents) is
                  permanently scrubbed.
                </ListItem>
                <ListItem>
                  <strong>This cannot be undone.</strong>
                </ListItem>
              </UnorderedList>

              <FormControl isRequired>
                <FormLabel htmlFor="delete-candidate-reason">Reason</FormLabel>
                <Textarea
                  id="delete-candidate-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Support ticket #1234 / GDPR erasure request"
                />
                <FormHelperText>
                  Kept for audit after the account is scrubbed, so use a
                  reference, not the candidate&apos;s personal details.
                </FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel htmlFor="delete-candidate-confirmation">
                  Type {CONFIRMATION_WORD} to confirm
                </FormLabel>
                <Input
                  id="delete-candidate-confirmation"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  autoComplete="off"
                />
                <FormHelperText>
                  Must match exactly (upper case).
                </FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                isDisabled={!canConfirm}
                isLoading={loading}
              >
                Permanently delete account
              </Button>
              <Button variant="outline" onClick={handleClose} isDisabled={loading}>
                Cancel
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default DeleteCandidateAccountButton;
